<script setup>
import { tv } from 'tailwind-variants';
import { computed } from 'vue';

/**
 * @typedef {object} CircularProgressProps
 * @property {number} [value=0] - The progress value from 0 to 100
 * @property {('sm'|'md'|'lg'|'xl'|'2xl'|'3xl')} [size='sm'] - Size of the circular progress indicator
 * @property {number} [sizeNumber=null] - Custom size in pixels (overrides size prop)
 * @property {('primary'|'critical')} [color='primary'] - Color variant of the progress indicator
 * @property {('faded'|'subtle')} [variant='faded'] - Style variant of the progress indicator
 */

/**
 * @type {CircularProgressProps}
 */
const props = defineProps({
  value: { type: Number, default: 0 },
  size: { type: String, default: 'sm' }, // 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl'
  sizeNumber: { type: Number, default: null },
  color: { type: String, default: 'primary' }, // 'primary' | 'critical' | 'warning' | 'success'
  variant: { type: String, default: 'faded' }, // 'faded' | 'subtle'
});

const circularProgressTheme = tv({
  slots: {
    track: 'fill-transparent',
    progress: 'fill-transparent transition-all duration-1000 ease-out',
  },
  variants: {
    color: {
      primary: {
        progress: 'stroke-brand-blue-11 dark:stroke-brand-blue-13',
      },
      critical: {
        progress: 'stroke-brand-red-12',
      },
    },
    variant: {
      faded: {},
      subtle: {},
    },
  },
  compoundVariants: [
    {
      color: ['primary', 'critical'],
      variant: 'faded',
      class: {
        track: 'stroke-brand-black-alpha-5 dark:stroke-brand-white-alpha-5',
      },
    },
    {
      color: 'primary',
      variant: 'subtle',
      class: {
        track: 'stroke-brand-azure-4 dark:stroke-brand-white-alpha-10',
      },
    },
    {
      color: 'critical',
      variant: 'subtle',
      class: {
        track: 'stroke-brand-red-4 dark:stroke-brand-white-alpha-10',
      },
    },
  ],
  defaultVariants: {
    color: 'primary',
    variant: 'faded',
  },
});

function calculatePercent(value) {
  if (value < 0) return 0;
  if (value > 100) return 100;
  return value;
}

function calculateStrokeDashArray(size) {
  return 2 * Math.PI * size;
}

function calculateStrokeDashOffset(percent, dashArray) {
  if (percent === 0) return dashArray;
  if (percent === 100) return 0;
  return ((100 - percent) / 100) * dashArray;
}

function calculateViewBoxSize(size, width) {
  return size / (1 - width / size);
}

function calculateStrokeWidth(width, size, viewBox) {
  return (width / size) * viewBox * 2;
}

function formatFinalViewBox(viewBox) {
  const result = [viewBox, viewBox, 2 * viewBox, 2 * viewBox];
  return result.join(' ');
}

const getSize = computed(() => {
  if (props.sizeNumber !== null && props.sizeNumber !== undefined) {
    return props.sizeNumber;
  }
  switch (props.size) {
    case 'sm':
      return 24;
    case 'md':
      return 28;
    case 'lg':
      return 32;
    case 'xl':
      return 36;
    case '2xl':
      return 40;
    case '3xl':
      return 44;
    default:
      return 28;
  }
});

const getPercent = computed(() => calculatePercent(props.value));
const getStrokeDashArray = computed(() =>
  calculateStrokeDashArray(getSize.value)
);
const getStrokeDashOffset = computed(() =>
  calculateStrokeDashOffset(getPercent.value, getStrokeDashArray.value)
);
const internalViewBoxSize = computed(() =>
  calculateViewBoxSize(getSize.value, getSize.value / 6)
);
const getStrokeWidth = computed(() =>
  calculateStrokeWidth(
    getSize.value / 6,
    getSize.value,
    internalViewBoxSize.value
  )
);
const getFinalViewBoxString = computed(() =>
  formatFinalViewBox(internalViewBoxSize.value)
);

const themeVariantClasses = computed(() => circularProgressTheme(props));
const trackClasses = computed(() => themeVariantClasses.value.track());
const progressClasses = computed(() => themeVariantClasses.value.progress());
</script>

<template>
  <div data-component="CsCircularProgress" class="relative inline-block">
    <svg
      class="-rotate-90"
      :style="{
        width: `${getSize}px`,
        height: `${getSize}px`,
      }"
      :viewBox="getFinalViewBoxString"
    >
      <circle
        :class="trackClasses"
        :cx="2 * internalViewBoxSize"
        :cy="2 * internalViewBoxSize"
        :r="getSize"
        :stroke-width="getStrokeWidth"
        :stroke-dasharray="getStrokeDashArray"
        stroke-dashoffset="0"
      />
      <circle
        :class="progressClasses"
        :cx="2 * internalViewBoxSize"
        :cy="2 * internalViewBoxSize"
        :r="getSize"
        :stroke-width="getStrokeWidth"
        stroke-linecap="round"
        :stroke-dasharray="getStrokeDashArray"
        :stroke-dashoffset="getStrokeDashOffset"
      />
    </svg>
    <slot />
  </div>
</template>
